import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'

import { AppRootState, Status } from 'src/store/store'
import type {
  AccreditationV2,
  CategoryResponse,
  EventAccreditation,
  Instructor,
  Template,
  TimeStamps,
} from 'src/v3/core/types'
import type {
  EventAttendeeRow,
  EventCapacityResponse,
  EventInviteStatus,
  EventSessionItem,
  RangeValue,
  ScopeValue,
  SessionOption,
} from './types'
import { categoryString } from './utils/category'
import {
  fetchEventAccreditationJoins,
  fetchEventAccreditations,
  fetchEventAttendees,
  fetchEventCategories,
  fetchEventInstructors,
  fetchEventSessions,
  fetchEventTemplateJoins,
  fetchEventCapacity,
  fetchEventTemplates,
  fetchEvents,
} from './utils/api'

// ─── Types ───────────────────────────────────────────────────────────────────

export type EventType =
  | {
      inPerson: {
        name: string
        address: string
        city: string
        state: string
        zip: string
        latitude: number | null
        longitude: number | null
      }
    }
  | { virtual: { name: string; url: string } }
  | {
      hybrid: [
        {
          name: string
          address: string
          city: string
          state: string
          zip: string
          latitude: number | null
          longitude: number | null
        },
        { name: string; url: string },
      ]
    }

export type ManageEvent = {
  id: string
  linkedServiceID: string
  banner: string | null
  thumbnail: string | null
  name: string
  description: string
  hours: number | null
  category: string | { other: string }
  attendanceTrackingMechanism: { quizPasscode: string | null }
  eventType: EventType
  featured: boolean
  lifecycleState: string
  createdTime: string
  updatedTime: string
  instructorIDs: string[]
  nextScheduledDate: string | null
  sessionCount: number
  registrationStatus:
    'registration-open' | 'registration-closed' | 'unscheduled'
  certTemplateName: string | null
}

export type ManageEventTemplate = Template

export type ManageEventTemplateJoin = {
  id: string
  eventID: string
  templateID: string
} & TimeStamps

export type ManageEventsFilters = {
  linkedServiceID: string
  category: string
  dateFrom: string | null
  dateTo: string | null
  registrationStatus: '' | 'registration-open' | 'registration-closed'
}

export type ManageEventsIndexTab = 'scheduled' | 'unscheduled'

// ─── State ───────────────────────────────────────────────────────────────────

type ManageEventsState = {
  events: ManageEvent[]
  eventsStatus: Status
  filters: ManageEventsFilters
  indexTab: ManageEventsIndexTab
  linkedServiceSeeded: boolean
  instructors: Instructor[]
  instructorsStatus: Status
  categories: CategoryResponse
  categoriesStatus: Status
  accreditations: AccreditationV2[]
  accreditationsStatus: Status
  eventAccreditations: EventAccreditation[]
  eventAccreditationsStatus: Status
  templates: ManageEventTemplate[]
  templatesStatus: Status
  eventTemplateJoins: ManageEventTemplateJoin[]
  eventTemplateJoinsStatus: Status
  sessions: EventSessionItem[]
  sessionsStatus: Status
  sessionsTotal: number
  sessionsRequestId: string | null
  attendees: EventAttendeeRow[]
  attendeesStatus: Status
  attendeesTotal: number
  // requestId of the latest fetch; older responses are dropped so a slow
  // request can't overwrite the view a newer one asked for.
  attendeesRequestId: string | null
  eventCapacity: EventCapacityResponse | null
  eventCapacityStatus: Status
  eventCapacityRequestId: string | null
  activeEventTab: number
  attendeeStatusPreset: string
  activeScope: ScopeValue
  activeRange: RangeValue
  activeSession: SessionOption | null
}

export let initialState: ManageEventsState = {
  events: [],
  eventsStatus: 'uninitialized',
  filters: {
    linkedServiceID: '',
    category: '',
    dateFrom: null,
    dateTo: null,
    registrationStatus: '',
  },
  indexTab: 'scheduled',
  linkedServiceSeeded: false,
  instructors: [],
  instructorsStatus: 'uninitialized',
  categories: { standard: [], custom: [] },
  categoriesStatus: 'uninitialized',
  accreditations: [],
  accreditationsStatus: 'uninitialized',
  eventAccreditations: [],
  eventAccreditationsStatus: 'uninitialized',
  templates: [],
  templatesStatus: 'uninitialized',
  eventTemplateJoins: [],
  eventTemplateJoinsStatus: 'uninitialized',
  sessions: [],
  sessionsStatus: 'uninitialized',
  sessionsTotal: 0,
  sessionsRequestId: null,
  attendees: [],
  attendeesStatus: 'uninitialized',
  attendeesTotal: 0,
  attendeesRequestId: null,
  eventCapacity: null,
  eventCapacityStatus: 'uninitialized',
  eventCapacityRequestId: null,
  activeEventTab: 0,
  attendeeStatusPreset: '',
  activeScope: 'upcoming',
  activeRange: '1m',
  activeSession: null,
}

// ─── Thunks ───────────────────────────────────────────────────────────────────

export let fetchManageEvents = createAsyncThunk(
  'manageEvents/fetchManageEvents',
  fetchEvents,
)

export let fetchManageEventAccreditations = createAsyncThunk(
  'manageEvents/fetchManageEventAccreditations',
  () =>
    fetchEventAccreditations().then((data) =>
      data.map((a): AccreditationV2 => {
        let [kind, obj] = Object.entries(a)[0]
        return { ...obj, kind: kind as any }
      }),
    ),
)

export let fetchManageEventAccreditationJoins = createAsyncThunk(
  'manageEvents/fetchManageEventAccreditationJoins',
  fetchEventAccreditationJoins,
)

export let fetchManageEventTemplates = createAsyncThunk(
  'manageEvents/fetchManageEventTemplates',
  fetchEventTemplates,
)

export let fetchManageEventTemplateJoins = createAsyncThunk(
  'manageEvents/fetchManageEventTemplateJoins',
  fetchEventTemplateJoins,
)

export let fetchManageEventInstructors = createAsyncThunk(
  'manageEvents/fetchManageEventInstructors',
  fetchEventInstructors,
)

export let fetchManageEventCategories = createAsyncThunk(
  'manageEvents/fetchManageEventCategories',
  fetchEventCategories,
)

export let fetchManageEventSessions = createAsyncThunk(
  'manageEvents/fetchManageEventSessions',
  ({
    eventID,
    page = 1,
    pageSize = 10,
    q,
    instructor,
    status,
    dateFrom,
    dateTo,
  }: {
    eventID: string
    page?: number
    pageSize?: number
    q?: string
    instructor?: string
    status?: string
    dateFrom?: string
    dateTo?: string
    // Refresh in place: keep the current cards (and focus inside them) mounted
    // instead of flipping to the loading skeleton. Read by the pending reducer.
    background?: boolean
  }) =>
    fetchEventSessions(eventID, page, pageSize, {
      q,
      instructor,
      status,
      dateFrom,
      dateTo,
    }).then((r) => ({
      items: r.items,
      total: r.total,
    })),
)

export let fetchManageEventAttendees = createAsyncThunk(
  'manageEvents/fetchManageEventAttendees',
  ({
    eventID,
    page = 1,
    pageSize = 10,
    scope,
    range,
    sessionID,
    q,
    status,
    license,
  }: {
    eventID: string
    page?: number
    pageSize?: number
    scope?: ScopeValue
    range?: RangeValue
    sessionID?: string
    q?: string
    status?: string
    license?: string
  }) =>
    fetchEventAttendees(eventID, page, pageSize, {
      scope,
      range,
      sessionId: sessionID,
      q,
      status,
      license,
    }).then((r) => ({ items: r.items, total: r.total })),
)

export let fetchManageEventCapacity = createAsyncThunk(
  'manageEvents/fetchManageEventCapacity',
  ({
    eventID,
    scope,
    range,
    sessionID,
  }: {
    eventID: string
    scope?: ScopeValue
    range?: RangeValue
    sessionID?: string
  }) => fetchEventCapacity(eventID, { scope, range, sessionId: sessionID }),
)

// ─── Async reducer helper ─────────────────────────────────────────────────────

type StatusKeys<T> = {
  [K in keyof T]: T[K] extends Status ? K : never
}[keyof T]
type DataKeys<T> = Exclude<keyof T, StatusKeys<T>>

let addAsyncReducers = <
  D extends DataKeys<ManageEventsState>,
  S extends StatusKeys<ManageEventsState>,
>(
  builder: ActionReducerMapBuilder<ManageEventsState>,
  thunk: any,
  dataKey: D,
  statusKey: S,
) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[statusKey] = 'pending' as Status
    })
    .addCase(thunk.rejected, (state) => {
      state[statusKey] = 'rejected' as Status
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[statusKey] = 'fulfilled' as Status
      state[dataKey] = action.payload as ManageEventsState[D]
    })
}

// ─── Slice ────────────────────────────────────────────────────────────────────

let manageEventsSlice = createSlice({
  name: 'manageEvents',
  initialState,
  reducers: {
    filtersChanged(state, action: PayloadAction<Partial<ManageEventsFilters>>) {
      state.filters = { ...state.filters, ...action.payload }
    },
    indexTabChanged(state, action: PayloadAction<ManageEventsIndexTab>) {
      state.indexTab = action.payload
    },
    linkedServiceFilterSeeded(state) {
      state.linkedServiceSeeded = true
    },
    sessionsReset(state) {
      state.sessions = []
      state.sessionsStatus = 'uninitialized'
      state.sessionsTotal = 0
      state.sessionsRequestId = null
      state.attendees = []
      state.attendeesStatus = 'uninitialized'
      state.attendeesTotal = 0
      state.attendeesRequestId = null
      state.eventCapacity = null
      state.eventCapacityStatus = 'uninitialized'
      state.eventCapacityRequestId = null
      state.activeScope = 'upcoming'
      state.activeRange = '1m'
      state.activeSession = null
      state.attendeeStatusPreset = ''
    },
    activeEventTabSet(state, action: PayloadAction<number>) {
      state.activeEventTab = action.payload
    },
    activeScopeSet(state, action: PayloadAction<ScopeValue>) {
      state.activeScope = action.payload
      state.activeRange = '1m'
      if (action.payload !== 'single') state.activeSession = null
    },
    activeRangeSet(state, action: PayloadAction<RangeValue>) {
      state.activeRange = action.payload
    },
    activeSessionSet(state, action: PayloadAction<SessionOption | null>) {
      state.activeSession = action.payload
      if (action.payload) state.activeScope = 'single'
    },
    attendeeStatusPresetSet(state, action: PayloadAction<string>) {
      state.attendeeStatusPreset = action.payload
    },
    attendeeStatusPresetCleared(state) {
      state.attendeeStatusPreset = ''
    },
    attendeeStatusChanged(
      state,
      action: PayloadAction<{ id: string; status: EventInviteStatus }>,
    ) {
      let row = state.attendees.find((r) => r.id === action.payload.id)
      if (row) {
        row.status = action.payload.status
      }
    },
  },
  extraReducers: (builder) => {
    addAsyncReducers(builder, fetchManageEvents, 'events', 'eventsStatus')
    addAsyncReducers(
      builder,
      fetchManageEventInstructors,
      'instructors',
      'instructorsStatus',
    )
    addAsyncReducers(
      builder,
      fetchManageEventCategories,
      'categories',
      'categoriesStatus',
    )
    addAsyncReducers(
      builder,
      fetchManageEventAccreditations,
      'accreditations',
      'accreditationsStatus',
    )
    addAsyncReducers(
      builder,
      fetchManageEventAccreditationJoins,
      'eventAccreditations',
      'eventAccreditationsStatus',
    )
    addAsyncReducers(
      builder,
      fetchManageEventTemplates,
      'templates',
      'templatesStatus',
    )
    addAsyncReducers(
      builder,
      fetchManageEventTemplateJoins,
      'eventTemplateJoins',
      'eventTemplateJoinsStatus',
    )

    builder
      .addCase(fetchManageEventSessions.pending, (state, action) => {
        // Background fetches count as the latest request too, so a refresh
        // started before a page or filter change can't land after it.
        state.sessionsRequestId = action.meta.requestId
        if (!action.meta.arg.background) state.sessionsStatus = 'pending'
      })
      .addCase(fetchManageEventSessions.rejected, (state, action) => {
        if (action.meta.requestId !== state.sessionsRequestId) return
        // A failed background refresh keeps the loaded cards (and the focus
        // target inside them) rather than replacing them with an error. Only
        // when they're loaded: if a superseded foreground fetch left the status
        // 'pending', the skeleton would otherwise never clear.
        if (action.meta.arg.background && state.sessionsStatus === 'fulfilled')
          return
        state.sessionsStatus = 'rejected'
      })
      .addCase(fetchManageEventSessions.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.sessionsRequestId) return
        state.sessionsStatus = 'fulfilled'
        state.sessions = action.payload.items
        state.sessionsTotal = action.payload.total
      })

    builder
      .addCase(fetchManageEventAttendees.pending, (state, action) => {
        state.attendeesStatus = 'pending'
        state.attendeesRequestId = action.meta.requestId
      })
      .addCase(fetchManageEventAttendees.rejected, (state, action) => {
        if (action.meta.requestId !== state.attendeesRequestId) return
        state.attendeesStatus = 'rejected'
      })
      .addCase(fetchManageEventAttendees.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.attendeesRequestId) return
        state.attendeesStatus = 'fulfilled'
        state.attendees = action.payload.items
        state.attendeesTotal = action.payload.total
      })

    builder
      .addCase(fetchManageEventCapacity.pending, (state, action) => {
        state.eventCapacityStatus = 'pending'
        state.eventCapacityRequestId = action.meta.requestId
      })
      .addCase(fetchManageEventCapacity.rejected, (state, action) => {
        if (action.meta.requestId !== state.eventCapacityRequestId) return
        state.eventCapacityStatus = 'rejected'
      })
      .addCase(fetchManageEventCapacity.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.eventCapacityRequestId) return
        state.eventCapacityStatus = 'fulfilled'
        state.eventCapacity = action.payload
      })
  },
})

export let {
  filtersChanged,
  indexTabChanged,
  linkedServiceFilterSeeded,
  sessionsReset,
  activeEventTabSet,
  attendeeStatusPresetSet,
  attendeeStatusPresetCleared,
  attendeeStatusChanged,
  activeScopeSet,
  activeRangeSet,
  activeSessionSet,
} = manageEventsSlice.actions

// ─── Selectors ────────────────────────────────────────────────────────────────

export let selectManageEvents = (state: AppRootState) =>
  state.manageEvents.events

export let selectManageEventsStatus = (state: AppRootState) =>
  state.manageEvents.eventsStatus

export let selectManageEventById =
  (id: string) =>
  (state: AppRootState): ManageEvent | undefined =>
    state.manageEvents.events.find((e) => e.id === id)

export let selectManageEventsFilters = (state: AppRootState) =>
  state.manageEvents.filters

export let selectManageEventsIndexTab = (state: AppRootState) =>
  state.manageEvents.indexTab

export let selectManageEventsLinkedServiceSeeded = (state: AppRootState) =>
  state.manageEvents.linkedServiceSeeded

export let selectManageEventInstructors = (state: AppRootState) =>
  state.manageEvents.instructors

export let selectManageEventInstructorsStatus = (state: AppRootState) =>
  state.manageEvents.instructorsStatus

export let selectManageEventCategories = (state: AppRootState) =>
  state.manageEvents.categories

export let selectManageEventCategoriesStatus = (state: AppRootState) =>
  state.manageEvents.categoriesStatus

export let selectManageEventAccreditations = (state: AppRootState) =>
  state.manageEvents.accreditations

export let selectManageEventAccreditationsStatus = (state: AppRootState) =>
  state.manageEvents.accreditationsStatus

export let selectManageEventAccreditationJoins = (state: AppRootState) =>
  state.manageEvents.eventAccreditations

export let selectManageEventAccreditationJoinsStatus = (state: AppRootState) =>
  state.manageEvents.eventAccreditationsStatus

export let selectManageEventTemplates = (state: AppRootState) =>
  state.manageEvents.templates

export let selectManageEventTemplatesStatus = (state: AppRootState) =>
  state.manageEvents.templatesStatus

export let selectManageEventTemplateJoins = (state: AppRootState) =>
  state.manageEvents.eventTemplateJoins

export let selectManageEventTemplateJoinsStatus = (state: AppRootState) =>
  state.manageEvents.eventTemplateJoinsStatus

export let selectTemplateLabelByEvent =
  (eventID: string) =>
  (state: AppRootState): string | null => {
    if (
      state.manageEvents.templatesStatus !== 'fulfilled' ||
      state.manageEvents.eventTemplateJoinsStatus !== 'fulfilled'
    )
      return null
    let join = state.manageEvents.eventTemplateJoins.find(
      (j) => j.eventID === eventID,
    )
    if (!join) return null
    let template = state.manageEvents.templates.find(
      (t) => t.id === join.templateID,
    )
    return template?.name ?? null
  }

export let selectAccreditationLabelByEvent =
  (eventID: string) =>
  (state: AppRootState): string | null => {
    if (
      state.manageEvents.accreditationsStatus !== 'fulfilled' ||
      state.manageEvents.eventAccreditationsStatus !== 'fulfilled'
    )
      return null
    let join = state.manageEvents.eventAccreditations.find(
      (j) => j.eventID === eventID,
    )
    if (!join) return null
    let accreditation = state.manageEvents.accreditations.find(
      (a) => a.id === join.accreditationID,
    )
    if (!accreditation) return null
    return accreditation.kind !== 'notAccredited'
      ? 'Accredited'
      : 'Not accredited'
  }

export { categoryString }

export let selectManageEventCategoryNameMap = createSelector(
  selectManageEventCategories,
  (categories) =>
    [...categories.standard, ...categories.custom].reduce<
      Record<string, string>
    >(
      (acc, key) => ({ ...acc, [key.toLowerCase()]: key.replace(/\|/g, '/') }),
      {},
    ),
)

export let selectManageEventInstructorMap = createSelector(
  selectManageEventInstructors,
  (instructors) =>
    instructors.reduce<Record<string, Instructor>>(
      (acc, i) => ({ ...acc, [i.id]: i }),
      {},
    ),
)

export let selectManageEventInstructorsByLinkedService = (
  linkedServiceID: string,
) =>
  createSelector(selectManageEventInstructors, (instructors) =>
    instructors.filter(
      (i) =>
        (linkedServiceID === 'FOAMfrat' && !i.linkedServiceID) ||
        i.linkedServiceID === linkedServiceID,
    ),
  )

export let isUnscheduledEvent = (e: Pick<ManageEvent, 'registrationStatus'>) =>
  e.registrationStatus === 'unscheduled'

// The linked service is the manager's scope (seeded from the active service), not a filter
// they chose, so empty states judge "filters hid everything" against this set.
export let selectServiceScopedManageEvents = createSelector(
  selectManageEvents,
  selectManageEventsFilters,
  (events, filters) =>
    events.filter(
      (e) =>
        !filters.linkedServiceID ||
        e.linkedServiceID === filters.linkedServiceID,
    ),
)

// Category applies on both index tabs; status and date range only on Scheduled, since
// unscheduled events have no next date and a single status.
let selectBaseFilteredManageEvents = createSelector(
  selectServiceScopedManageEvents,
  selectManageEventsFilters,
  (events, filters) =>
    events.filter(
      (e) =>
        !filters.category ||
        categoryString(e.category).toLowerCase() ===
          filters.category.toLowerCase(),
    ),
)

export let selectScheduledManageEvents = createSelector(
  selectBaseFilteredManageEvents,
  selectManageEventsFilters,
  (events, filters) =>
    events.filter((e) => {
      if (isUnscheduledEvent(e)) return false
      if (
        filters.registrationStatus &&
        e.registrationStatus !== filters.registrationStatus
      )
        return false
      if (filters.dateFrom || filters.dateTo) {
        if (!e.nextScheduledDate) return false
        if (
          filters.dateFrom &&
          e.nextScheduledDate.slice(0, 10) < filters.dateFrom
        )
          return false
        if (filters.dateTo && e.nextScheduledDate.slice(0, 10) > filters.dateTo)
          return false
      }
      return true
    }),
)

export let selectUnscheduledManageEvents = createSelector(
  selectBaseFilteredManageEvents,
  (events) => events.filter(isUnscheduledEvent),
)

export let selectManageEventSessions = (
  state: AppRootState,
): EventSessionItem[] => state.manageEvents.sessions

export let selectManageEventSessionsStatus = (state: AppRootState): Status =>
  state.manageEvents.sessionsStatus

export let selectManageEventSessionsTotal = (state: AppRootState): number =>
  state.manageEvents.sessionsTotal

export let selectManageEventAttendees = (
  state: AppRootState,
): EventAttendeeRow[] => state.manageEvents.attendees

export let selectManageEventAttendeesStatus = (state: AppRootState): Status =>
  state.manageEvents.attendeesStatus

export let selectManageEventAttendeesTotal = (state: AppRootState): number =>
  state.manageEvents.attendeesTotal

export let selectManageEventCapacity = (
  state: AppRootState,
): EventCapacityResponse | null => state.manageEvents.eventCapacity

export let selectManageEventCapacityStatus = (state: AppRootState): Status =>
  state.manageEvents.eventCapacityStatus

export let selectActiveEventTab = (state: AppRootState): number =>
  state.manageEvents.activeEventTab

export let selectAttendeeStatusPreset = (state: AppRootState): string =>
  state.manageEvents.attendeeStatusPreset

export let selectActiveScope = (state: AppRootState): ScopeValue =>
  state.manageEvents.activeScope

export let selectActiveRange = (state: AppRootState): RangeValue =>
  state.manageEvents.activeRange

export let selectActiveSession = (state: AppRootState): SessionOption | null =>
  state.manageEvents.activeSession

export default manageEventsSlice.reducer
